import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ColumnDef} from "@tanstack/react-table";
import {DataTable} from "@/lib/components/DataTable";
import {QuoteStatusByCompanyDTO} from "@/api/data-contracts";
import {CommonI18N, ReportI18N} from "@/translations";

interface QuoteStatusByCompanyReportProps {
    data: QuoteStatusByCompanyDTO[];
}

interface QuoteStatusByCompanyRow extends QuoteStatusByCompanyDTO {
    id: number;
}

/**
 * Report table showing quote counts grouped by supplier company and status.
 */
const QuoteStatusByCompanyReport: React.FC<QuoteStatusByCompanyReportProps> = ({ data }) => {
    const { t } = useTranslation();

    const rows = useMemo<QuoteStatusByCompanyRow[]>(
        () => data.map((item, index) => ({ ...item, id: index })),
        [data]
    );

    const columns = useMemo<ColumnDef<QuoteStatusByCompanyRow>[]>(
        () => [
            {
                header: t(ReportI18N.page.column.company),
                accessorFn: (row) => row.companyName ?? "",
                id: "companyName"
            },
            {
                header: t(ReportI18N.page.column.status),
                accessorFn: (row) => (row.status ? t(CommonI18N.enum.quoteStatus[row.status]) : ""),
                id: "status"
            },
            {
                header: t(ReportI18N.page.column.count),
                accessorFn: (row) => row.count ?? 0,
                id: "count"
            }
        ],
        [t]
    );

    return <DataTable columns={columns} data={rows} noResultsValue="" striped maxHeight={940} />;
};

export default QuoteStatusByCompanyReport;
